import React, { useCallback, useEffect, useMemo } from "react";
import { Button, Row } from "uikit";
import { useTranslation } from "react-i18next";

import archiveReportsByOrders from "../../../../../../../../redux/reducers/Archives/Reports/charts/byOrders";
import {
	useTypedDispatch,
	useTypedSelector,
} from "../../../../../../../../redux/store";
import {
	Calendar,
	AccessGuard,
	StyledRow,
} from "../../../../../../../../components/common";
import HeaderBase from "../../../../../../../../components/BasicPageLayout/Header";
import CompaniesAndTaxiServicesFilter from "../../../../../../../../components/CompaniesAndTaxiServicesFilter";
import { useDatePickerLocale } from "../../../../../../../../hooks";
import { TabAccessNames, TabKeys } from "../../constants/access";
import { useAppContext } from "../../../../../../../../context";

import ChartTypeSelect from "./ChartTypeSelect";
import IntervalSelect from "./IntervalSelect";

const Header: React.FC<Header.Props> = ({
	dateFrom,
	dateTo,
	filter,

	loading,
	fetchOrders,
}) => {
	const { t } = useTranslation();
	const { dateFns } = useAppContext();
	const dispatch = useTypedDispatch();
	const locale = useDatePickerLocale();
	const globalLang = useTypedSelector((state) => state.session.language);

	const handleFromChange = useCallback(
		(newDate) => {
			if (newDate)
				dispatch(
					archiveReportsByOrders.actions.setDateFrom(
						newDate.getTime(),
					),
				);
		},
		[dispatch],
	);

	const handleToChange = useCallback(
		(newDate) => {
			if (newDate)
				dispatch(
					archiveReportsByOrders.actions.setDateTo(newDate.getTime()),
				);
		},
		[dispatch],
	);

	useEffect(() => {
		if (dateFns) {
			handleFromChange(dateFns.startDate());
			handleToChange(dateFns.endDate());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dateFns?.timezone]);

	return (
		<HeaderBase>
			<StyledRow justify="center" alignItems="center" w="100%" gap="5px">
				<AccessGuard
					accessName={TabAccessNames[TabKeys.DATE]}
					fallbackWrapper
				>
					{t(`from`, { context: "capitalized" })}
					<Calendar
						value={useMemo(() => new Date(dateFrom), [dateFrom])}
						onChange={handleFromChange}
						locale={locale}
						withTime
					/>
					{t(`to`)}
					<Calendar
						value={useMemo(() => new Date(dateTo), [dateTo])}
						onChange={handleToChange}
						locale={locale}
						withTime
					/>
				</AccessGuard>

				<Row
					style={{
						flexGrow: 1,
						maxWidth: "800px",
					}}
					gaps="5px*"
					align="center"
					sizes="1fr!*"
				>
					<AccessGuard
						accessName={
							TabAccessNames[TabKeys.COMPANIES_AND_TAXI_SERVICES]
						}
						fallbackWrapper
					>
						<CompaniesAndTaxiServicesFilter
							companies={filter.companies}
							taxiServices={filter.taxiServices}
							language={globalLang}
							onChangeCompanies={(companyFilter) => {
								dispatch(
									archiveReportsByOrders.actions.setCompanyFilter(
										companyFilter,
									),
								);
							}}
							onChangeTaxiServices={(taxiServiceFilter) => {
								dispatch(
									archiveReportsByOrders.actions.setTaxiServiceFilter(
										taxiServiceFilter,
									),
								);
							}}
						/>
					</AccessGuard>

					<AccessGuard
						accessName={TabAccessNames[TabKeys.GENERATE]}
						fallbackWrapper
					>
						<Button.Button
							variant="primary"
							text={t(`generate`) || "Generate"}
							onClick={fetchOrders}
							disabled={loading}
						/>
					</AccessGuard>

					<AccessGuard
						accessName={TabAccessNames[TabKeys.CHART_TYPE]}
						fallbackWrapper
					>
						<ChartTypeSelect
							value={filter.chartType}
							onChange={(intervalLabel) => {
								// setChartType(intervalLabel);
								dispatch(
									archiveReportsByOrders.actions.setChartType(
										intervalLabel,
									),
								);
							}}
						/>
					</AccessGuard>

					<AccessGuard
						accessName={TabAccessNames[TabKeys.TIME_TYPE]}
						fallbackWrapper
					>
						<IntervalSelect
							value={filter.interval.label}
							onChange={(intervalLabel) => {
								dispatch(
									archiveReportsByOrders.actions.setInterval(
										intervalLabel,
									),
								);
							}}
							disabled={filter.chartType === "circular"}
						/>
					</AccessGuard>
				</Row>
			</StyledRow>
		</HeaderBase>
	);
};

declare namespace Header {
	interface Props {
		dateFrom: number;
		dateTo: number;
		filter: any;

		loading: boolean;
		fetchOrders: () => void;
	}
}

export default Header;
